import { Skeleton, Text } from '@mantine/core'
import { ReactQuery } from 'cms-api'
import Link from 'next/link'
import React from 'react'
import { Button, BUTTON_VARIANT } from 'components/Button'
import { Card, CARD_ELEVATION, CARD_PADDING_SIZE } from 'components/Card'
import { Box, Flex } from 'components/Layout'
import { COLOR_NAMES, SPACE } from 'Theme'

const getInitials = (name?: string | null) =>
  (name ?? '')
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

const MemberSkeleton = () => (
  <Box>
    <Flex alignItems="center" gap={16} mb={SPACE.PX_20}>
      <Skeleton height={56} width={56} radius="50%" />
      <Box style={{ flex: 1 }}>
        <Skeleton height={10} width={100} mb={8} />
        <Skeleton height={18} width={160} mb={6} />
        <Skeleton height={12} width={200} />
      </Box>
    </Flex>
    <Flex gap={SPACE.PX_8}>
      <Skeleton height={40} width="100%" radius="xl" />
      <Skeleton height={40} width="100%" radius="xl" />
    </Flex>
  </Box>
)

const MemberCard = ({
  avatarUrl,
  title,
  name,
  email,
  phone,
}: ReactQuery.ExpertDashboardTeamMember) => (
  <Box>
    <Flex alignItems="center" gap={16} mb={SPACE.PX_20}>
      {avatarUrl ? (
        <Box
          width={56}
          minWidth={56}
          height={56}
          borderRadius="50%"
          style={{
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundImage: `url(${avatarUrl})`,
          }}
        />
      ) : (
        <Flex
          alignItems="center"
          justifyContent="center"
          width={56}
          minWidth={56}
          height={56}
          borderRadius="50%"
          backgroundColor={COLOR_NAMES.WATER_BAPTISM}
        >
          <Text fz={20} fw={600} c={COLOR_NAMES.PINEY_LAKE}>
            {getInitials(name)}
          </Text>
        </Flex>
      )}
      <Box style={{ minWidth: 0 }}>
        {title && (
          <Text
            tt="uppercase"
            lts="0.1em"
            fz={11}
            fw={600}
            c={COLOR_NAMES.PINEY_LAKE}
          >
            {title}
          </Text>
        )}
        <Text fz={16} fw={600} c={COLOR_NAMES.EEIRE_BLACK}>
          {name || 'Your account manager'}
        </Text>
        {(email || phone) && (
          <Text fz={13} c={COLOR_NAMES.CARBON_COPY}>
            {[email, phone].filter(Boolean).join(' · ')}
          </Text>
        )}
      </Box>
    </Flex>
    <Flex gap={SPACE.PX_8}>
      {phone && (
        <Box style={{ flex: 1 }}>
          <Link href={`tel:${phone}`} style={{ display: 'block' }}>
            <Button variant={BUTTON_VARIANT.FILLED}>Book a call</Button>
          </Link>
        </Box>
      )}
      {email && (
        <Box style={{ flex: 1 }}>
          <Link href={`mailto:${email}`} style={{ display: 'block' }}>
            <Button variant={BUTTON_VARIANT.OUTLINE}>Send a note</Button>
          </Link>
        </Box>
      )}
    </Flex>
  </Box>
)

export const TeamWidget = () => {
  const { data, isLoading } = ReactQuery.useGetExpertDashboardTeam()

  return (
    <Card
      flexDirection="column"
      width="100%"
      height="100%"
      elevation={CARD_ELEVATION.NONE}
      hasBorders
      paddingSize={CARD_PADDING_SIZE.NONE}
      overflow="hidden"
    >
      <Box
        width="100%"
        height="100%"
        p={SPACE.PX_30}
        borderRadius={SPACE.PX_8}
        style={{
          background: 'linear-gradient(135deg, #EBF2F1 0%, #C8D8D5 100%)',
        }}
      >
        <Text
          fz={24}
          fw={500}
          c={COLOR_NAMES.EEIRE_BLACK}
          component="h2"
          mb={SPACE.PX_20}
        >
          Your Expert Team
        </Text>

        {isLoading && <MemberSkeleton />}

        {!isLoading && data?.members?.length > 0 && (
          <Flex flexDirection="column" gap={SPACE.PX_30}>
            {data.members.map((member, index) => (
              <MemberCard key={member.name ?? index} {...member} />
            ))}
          </Flex>
        )}

        {!isLoading && (!data?.members || data.members.length === 0) && (
          <Text c={COLOR_NAMES.EEIRE_BLACK}>
            You have no team members added yet.
          </Text>
        )}
      </Box>
    </Card>
  )
}
